import { useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import workspaceFeaturesCopy from '@/i18n/ns/workspace-features';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import { projectHomeId } from './home';
import { lastActive } from './labels';
import { ProjectMark } from './mark';
import { openNewProject } from './new-project-request';
import { chatProject } from './place';
import { useProjects } from './use-projects';

// Projects in the sidebar: a section you pick from (working inside a project, 2026-10-16).
// "Projects ▾" folds it, + makes a new one, the latest projects follow with the one you are
// in highlighted, and All projects opens the overview with every project.

const SHOWN = 3;
const OPEN_KEY = 'poneva.workspace.projects-open';

function readOpen() {
  try {
    return localStorage.getItem(OPEN_KEY) !== 'false';
  } catch {
    return true;
  }
}

/** The project you are in: its home, or a chat that lives in it. Leaving it ends the highlight. */
function useCurrentProject() {
  const ws = useWorkspace();
  if (ws.view !== 'chat') return null;
  return projectHomeId(ws) ?? (ws.threadSaved ? (chatProject(ws)?.id ?? null) : null);
}

export function ProjectsNavSection() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceFeaturesCopy);
  const [open, setOpen] = useState(readOpen);
  const { projects, loaded } = useProjects();
  const current = useCurrentProject();
  const toggle = () => {
    // A collapsed sidebar has no room for the list: the icon opens All projects.
    if (ws.compact) return ws.openProject(null);
    setOpen((value) => {
      try {
        localStorage.setItem(OPEN_KEY, String(!value));
      } catch {}
      return !value;
    });
  };
  const active = projects
    .filter((project) => !project.finished)
    .sort((a, b) => lastActive(b).localeCompare(lastActive(a)));
  const shown = active.slice(0, SHOWN);
  const here = current ? projects.find((project) => project.id === current) : undefined;
  if (here && !shown.includes(here)) shown.push(here);
  return (
    <div className="ws-nav-projects" data-testid="sidebar-projects">
      <div className="ws-nav-projects-head">
        <button
          type="button"
          className={`ws-nav-item ws-nav-projects-toggle ${current ? 'in-project' : ''} ${ws.compact && ws.view === 'projects' ? 'active' : ''}`}
          aria-expanded={ws.compact ? undefined : open}
          aria-current={ws.compact && ws.view === 'projects' ? 'page' : undefined}
          title={copy.projects}
          onClick={toggle}
        >
          <WorkspaceIcon name="project" />
          <span>
            {copy.projects}
            <WorkspaceIcon name="down" size={12} className="ws-nav-projects-chevron" />
          </span>
        </button>
        <button
          type="button"
          className="ws-nav-projects-add"
          aria-label={copy.newProject}
          title={copy.newProject}
          onClick={() => openNewProject()}
        >
          <WorkspaceIcon name="plus" size={14} />
        </button>
      </div>
      {open && !ws.compact && (
        <div className="ws-nav-projects-list">
          {loaded &&
            shown.map((project) => (
              <button
                key={project.id}
                type="button"
                className={`ws-nav-item ws-nav-project ${project.id === current ? 'active' : ''}`}
                aria-current={project.id === current ? 'page' : undefined}
                title={project.name}
                onClick={() => ws.openProject(project.id)}
              >
                <ProjectMark name={project.name} tint={project.tint} size="xs" />
                <span>{project.name}</span>
              </button>
            ))}
          <button
            type="button"
            className={`ws-nav-item ws-nav-project ws-nav-projects-all ${ws.view === 'projects' ? 'active' : ''}`}
            aria-current={ws.view === 'projects' ? 'page' : undefined}
            onClick={() => ws.openProject(null)}
          >
            <span>{copy.allProjects}</span>
          </button>
        </div>
      )}
    </div>
  );
}
